import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaChartBar, FaEraser } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import PosicaoSinteticaService from './posicaoSinteticaService';
import ResultadoTabela from './ResultadoTabela';

// W-0613 — Posição Sintética Carteira a Receber (GES > Gerencial > Finanças)
// Fonte: UposicaoSintetica.pas (TFposicaoSintetica). Server-side, sem SSRS/.rdl.
// Filtros: moeda de referência (Lmo) + multi-GE (TFGerencial) + "Levanta correção" (chCor, default ON).

const extrairMensagemErro = (err, fallback) =>
  (err && (err.message || err.Mensagem)) || fallback;

const PosicaoSinteticaCarteiraReceber = () => {
  const [moedas, setMoedas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [carregandoListas, setCarregandoListas] = useState(true);

  const [codigoMoeda, setCodigoMoeda] = useState(null);
  const [gesSelecionados, setGesSelecionados] = useState([]);
  const [levantarCorrecao, setLevantarCorrecao] = useState(true); // replica chCor.Checked=True

  const [resultado, setResultado] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let ativo = true;
    setCarregandoListas(true);
    Promise.all([
      PosicaoSinteticaService.listarMoedas(),
      PosicaoSinteticaService.listarGruposEmpresa(),
    ])
      .then(([resMoedas, resGrupos]) => {
        if (!ativo) return;
        setMoedas(Array.isArray(resMoedas) ? resMoedas : []);
        setGrupos(Array.isArray(resGrupos) ? resGrupos : []);
      })
      .catch((err) => {
        if (!ativo) return;
        const msg = extrairMensagemErro(err, 'Erro ao carregar moedas e grupos de empresa.');
        setErro(msg);
        toast.error(msg);
      })
      .then(() => {
        if (ativo) setCarregandoListas(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const opcoesMoeda = moedas.map((m) => ({
    value: m.codigo_ativo,
    label: `${m.codigo_ativo} - ${m.nome}`,
  }));
  const opcoesGE = grupos.map((g) => ({
    value: g.codigo_grupo_empresa,
    label: `${g.codigo_grupo_empresa} - ${g.apelido}`,
  }));

  const moedaSelecionada = opcoesMoeda.find((o) => o.value === codigoMoeda) || null;
  const gesSelecionadosOpts = opcoesGE.filter((o) => gesSelecionados.includes(o.value));

  // Botão Limpar (NEWFEATURE Q-06=B — morto no Delphi): limpa moeda, GEs e checkbox.
  const handleLimpar = () => {
    setCodigoMoeda(null);
    setGesSelecionados([]);
    setLevantarCorrecao(true);
    setResultado(null);
    setErro('');
  };

  const handleGerar = async () => {
    setErro('');
    // RN-1 (UposicaoSintetica.pas:66): moeda obrigatória — literal Delphi.
    if (!codigoMoeda) {
      const msg = 'Moeda inválida!';
      setErro(msg);
      toast.warn(msg);
      return;
    }
    // RN-2 (UposicaoSintetica.pas:72): pelo menos 1 GE — literal Delphi.
    if (!gesSelecionados.length) {
      const msg = 'Nenhum Grupo de empresas selecionado!';
      setErro(msg);
      toast.warn(msg);
      return;
    }

    setGerando(true);
    setResultado(null);
    try {
      const res = await PosicaoSinteticaService.gerar({
        codigosGrupoEmpresa: gesSelecionados,
        codigoMoeda,
        levantarCorrecao,
      });
      setResultado(res || { linhas: [] });
    } catch (err) {
      const msg = extrairMensagemErro(err, 'Não foi possível gerar o relatório.');
      setErro(msg);
      toast.error(msg);
    } finally {
      setGerando(false);
    }
  };

  const handleExportar = async () => {
    if (!resultado || !resultado.linhas || resultado.linhas.length === 0) return;
    setExportando(true);
    try {
      await PosicaoSinteticaService.exportarExcel(resultado);
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro ao exportar relatório.'));
    } finally {
      setExportando(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <Header title="Posição Sintética Carteira a Receber" />

      <Card className="mt-3">
        <Card.Body>
          {carregandoListas ? (
            <Loading label="Carregando filtros..." />
          ) : (
            <Row>
              {/* Coluna de filtros */}
              <Col xs={12} md={5} lg={4}>
                <Form.Group>
                  <Form.Label>
                    Moeda referência <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    options={opcoesMoeda}
                    value={moedaSelecionada}
                    onChange={(opt) => setCodigoMoeda(opt ? opt.value : null)}
                    placeholder="Selecione a moeda..."
                    noOptionsMessage={() => 'Nenhuma moeda disponível'}
                    isClearable
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="chk-levantar-correcao"
                    label="Levanta correção"
                    checked={levantarCorrecao}
                    onChange={(e) => setLevantarCorrecao(e.target.checked)}
                  />
                </Form.Group>

                <div className="d-flex">
                  <Button
                    variant="primary"
                    onClick={handleGerar}
                    disabled={gerando}
                    className="mr-2"
                  >
                    <FaChartBar className="mr-2" />
                    {gerando ? 'Gerando...' : 'OK'}
                  </Button>
                  <Button variant="outline-secondary" onClick={handleLimpar} disabled={gerando}>
                    <FaEraser className="mr-2" />
                    Limpar
                  </Button>
                </div>
              </Col>

              {/* Coluna do multi-GE picker */}
              <Col xs={12} md={7} lg={8}>
                <Form.Group>
                  <Form.Label>
                    Grupos de Empresa <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    isMulti
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    options={opcoesGE}
                    value={gesSelecionadosOpts}
                    onChange={(opts) =>
                      setGesSelecionados(opts ? opts.map((o) => o.value) : [])
                    }
                    placeholder="Selecione um ou mais GEs..."
                    noOptionsMessage={() => 'Nenhum GE disponível'}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {gerando && <Loading label="Levantando correção..." />}

      {!gerando && erro && (
        <Alert variant="warning" className="mt-3">
          {erro}
        </Alert>
      )}

      {!gerando && resultado && (
        <ResultadoTabela
          resultado={resultado}
          onExportar={handleExportar}
          exportando={exportando}
        />
      )}
    </Container>
  );
};

export default PosicaoSinteticaCarteiraReceber;
